/**
 * Quick Stats Widget for Command Center Dashboard
 * Shows 3 live metric cards with 30-day trend indicators
 * Clickable cards navigate to full Analytics page
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== STAT CARD COMPONENT ====================
const QuickStatCard = ({ stat, icon: Icon, onClick }) => {
  const getTrendIcon = () => {
    if (!stat.trend || stat.trend === 'neutral') {
      return <Minus className="w-4 h-4 text-soft-silver" />;
    }
    if (stat.trend === 'up') {
      return <TrendingUp className="w-4 h-4 text-cool-mint" />;
    }
    return <TrendingDown className="w-4 h-4 text-signal-red" />;
  };

  const getTrendColor = () => {
    if (!stat.trend || stat.trend === 'neutral') return 'text-soft-silver';
    return stat.trend === 'up' ? 'text-cool-mint' : 'text-signal-red';
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "p-5 bg-graphite/50 rounded-xl border border-white/[0.06]",
        "hover:border-electric-ice-blue/30 hover:bg-graphite/70",
        "cursor-pointer transition-all group"
      )}
      data-testid={`quick-stat-${stat.key}`}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-electric-ice-blue" />
        </div>
        <div className="flex items-center gap-1">
          {getTrendIcon()}
          {stat.trend_value && (
            <span className={cn("text-xs font-medium", getTrendColor())}>
              {stat.trend === 'up' ? '+' : ''}{stat.trend_value}
            </span>
          )}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-3xl font-mono font-bold text-cloud-white">
          {stat.prefix || ''}{stat.value}{stat.suffix || ''}
        </p>
        <p className="text-sm text-soft-silver mt-1">{stat.label}</p>
      </div>

      <div className="mt-3 flex items-center gap-1 text-xs text-muted-text group-hover:text-electric-ice-blue transition-colors">
        <span>View details</span>
        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  );
};

// ==================== MAIN WIDGET COMPONENT ====================
export const QuickStatsWidget = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();
  const navigate = useNavigate();

  // Only show to owner and admin
  const canView = hasOrgRole(['owner', 'admin']);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!organization?.id || !session?.access_token || !canView) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/analytics/overview/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch quick stats:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token, canView]);

  useEffect(() => {
    fetchStats();

    // Set up polling for real-time updates (every 30 seconds)
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Don't render for non-admin users
  if (!canView) return null;

  // Loading state
  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-cloud-white">Quick Stats</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06] animate-pulse">
              <div className="w-10 h-10 rounded-lg bg-white/[0.05]" />
              <div className="mt-4 h-8 w-20 bg-white/[0.05] rounded" />
              <div className="mt-2 h-4 w-24 bg-white/[0.05] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // No stats available
  if (!stats) return null;

  // Extract the three main stats
  const quickStats = [
    {
      key: 'active_members',
      ...stats.active_members,
      icon: Users,
    },
    {
      key: 'event_attendance_rate',
      ...stats.event_attendance_rate,
      icon: Calendar,
    },
    {
      key: 'dues_collection_rate',
      ...stats.dues_collection_rate,
      icon: DollarSign,
    },
  ].filter(s => s.value !== undefined);

  if (quickStats.length === 0) return null;

  return (
    <div className="mb-6" data-testid="quick-stats-widget">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-cloud-white">Quick Stats</h2>
        <button
          onClick={() => navigate('/app/analytics')}
          className="text-sm text-electric-ice-blue hover:underline flex items-center gap-1"
        >
          View All Analytics
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickStats.map((stat) => (
          <QuickStatCard
            key={stat.key}
            stat={stat}
            icon={stat.icon}
            onClick={() => navigate('/app/analytics')}
          />
        ))}
      </div>
    </div>
  );
};

export default QuickStatsWidget;
